import React, { useState, useRef, useEffect } from 'react';
import { SQL_TABLES } from '../data';

function parseAndExecuteSQL(query) {
  const q = query.trim().toLowerCase().replace(/;$/, '');

  // SHOW TABLES
  if (q === 'show tables') {
    return { columns: ['table_name'], rows: Object.keys(SQL_TABLES).map(t => [t]) };
  }

  // DESCRIBE table
  const descMatch = q.match(/^desc(?:ribe)?\s+(\w+)$/);
  if (descMatch) {
    const table = SQL_TABLES[descMatch[1]];
    if (!table || !table.length) return { error: `Table '${descMatch[1]}' not found. Try: SHOW TABLES` };
    const cols = Object.keys(table[0]);
    return { columns: ['column_name', 'type'], rows: cols.map(c => [c, typeof table[0][c]]) };
  }

  // SELECT
  const selectMatch = q.match(/^select\s+(.+?)\s+from\s+(\w+)(?:\s+where\s+(.+?))?(?:\s+order\s+by\s+(.+?))?(?:\s+limit\s+(\d+))?$/);
  if (selectMatch) {
    const [, selectPart, tableName, wherePart, orderPart, limitPart] = selectMatch;
    const table = SQL_TABLES[tableName];
    if (!table || !table.length) return { error: `Table '${tableName}' not found. Try: SHOW TABLES` };

    const allCols = Object.keys(table[0]);
    const columns = selectPart.trim() === '*' ? allCols : selectPart.split(',').map(c => c.trim());

    // Validate columns
    for (const col of columns) {
      if (!allCols.includes(col)) return { error: `Column '${col}' not found in '${tableName}'. Columns: ${allCols.join(', ')}` };
    }

    let rows = [...table];

    // WHERE (simple: col = 'val', col > val, col LIKE '%val%')
    if (wherePart) {
      const likeMatch = wherePart.match(/(\w+)\s+like\s+'%(.+?)%'/);
      const eqMatch = wherePart.match(/(\w+)\s*=\s*'?(.+?)'?\s*$/);
      const gtMatch = wherePart.match(/(\w+)\s*>\s*(\d+)/);
      const ltMatch = wherePart.match(/(\w+)\s*<\s*(\d+)/);
      const isNullMatch = wherePart.match(/(\w+)\s+is\s+null/);
      const isNotNullMatch = wherePart.match(/(\w+)\s+is\s+not\s+null/);

      if (likeMatch) {
        const [, col, val] = likeMatch;
        rows = rows.filter(r => String(r[col] || '').toLowerCase().includes(val.toLowerCase()));
      } else if (isNotNullMatch) {
        rows = rows.filter(r => r[isNotNullMatch[1]] !== null && r[isNotNullMatch[1]] !== undefined);
      } else if (isNullMatch) {
        rows = rows.filter(r => r[isNullMatch[1]] === null || r[isNullMatch[1]] === undefined);
      } else if (gtMatch) {
        rows = rows.filter(r => Number(r[gtMatch[1]]) > Number(gtMatch[2]));
      } else if (ltMatch) {
        rows = rows.filter(r => Number(r[ltMatch[1]]) < Number(ltMatch[2]));
      } else if (eqMatch) {
        const [, col, val] = eqMatch;
        const cleanVal = val.replace(/'/g, '');
        rows = rows.filter(r => String(r[col]).toLowerCase() === cleanVal.toLowerCase());
      }
    }

    // ORDER BY
    if (orderPart) {
      const [col, dir] = orderPart.trim().split(/\s+/);
      const desc = dir && dir.toLowerCase() === 'desc';
      rows.sort((a, b) => {
        if (a[col] < b[col]) return desc ? 1 : -1;
        if (a[col] > b[col]) return desc ? -1 : 1;
        return 0;
      });
    }

    // LIMIT
    if (limitPart) rows = rows.slice(0, parseInt(limitPart));

    return { columns, rows: rows.map(r => columns.map(c => r[c] ?? 'NULL')) };
  }

  // COUNT
  const countMatch = q.match(/^select\s+count\(\*\)\s+from\s+(\w+)$/);
  if (countMatch) {
    const table = SQL_TABLES[countMatch[1]];
    if (!table) return { error: `Table '${countMatch[1]}' not found.` };
    return { columns: ['count'], rows: [[table.length]] };
  }

  return { error: "Could not parse query. Try: SELECT * FROM experience; or SHOW TABLES;" };
}

export default function SQLEasterEgg({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setResults([{ type: 'system', text: "SQL Mode — Query Mekhal's career data. Try: SELECT * FROM experience;" }]);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [results]);

  const execute = () => {
    if (!query.trim()) return;
    const newResults = [...results, { type: 'query', text: query }];
    const result = parseAndExecuteSQL(query);

    if (result.error) {
      newResults.push({ type: 'error', text: result.error });
    } else {
      newResults.push({ type: 'table', columns: result.columns, rows: result.rows });
    }
    setResults(newResults);
    setQuery('');
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2600,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: 'min(800px, 92vw)', maxHeight: '75vh',
        background: '#0a0a0a', border: '1px solid rgba(91,143,196,0.3)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'var(--font-mono)',
      }}>
        {/* Header */}
        <div style={{
          padding: '10px 16px', borderBottom: '1px solid rgba(250,250,248,0.08)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.2em', color: '#5b8fc4', textTransform: 'uppercase' }}>
            SQL Query Engine · mekhal_db
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(250,250,248,0.3)', cursor: 'pointer', fontSize: '12px' }}>ESC</button>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', minHeight: '200px', maxHeight: '500px' }}>
          {results.map((r, i) => {
            if (r.type === 'system') return <div key={i} style={{ fontSize: '11px', color: 'rgba(250,250,248,0.35)', marginBottom: '12px', lineHeight: 1.6 }}>{r.text}</div>;
            if (r.type === 'query') return <div key={i} style={{ fontSize: '11px', color: '#5b8fc4', marginBottom: '4px' }}>{'>'} {r.text}</div>;
            if (r.type === 'error') return <div key={i} style={{ fontSize: '11px', color: '#ff6b6b', marginBottom: '12px' }}>{r.text}</div>;
            if (r.type === 'table') {
              return (
                <div key={i} style={{ marginBottom: '16px', overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px' }}>
                    <thead>
                      <tr>
                        {r.columns.map((col, j) => (
                          <th key={j} style={{
                            textAlign: 'left', padding: '6px 16px 6px 0',
                            borderBottom: '1px solid rgba(250,250,248,0.15)',
                            color: '#5b8fc4', fontWeight: 500, letterSpacing: '0.1em',
                            textTransform: 'uppercase', whiteSpace: 'nowrap',
                          }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {r.rows.map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci} style={{
                              padding: '5px 16px 5px 0',
                              borderBottom: '1px solid rgba(250,250,248,0.05)',
                              color: 'rgba(250,250,248,0.7)', whiteSpace: 'nowrap',
                            }}>{String(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: '10px', color: 'rgba(250,250,248,0.25)', marginTop: '6px' }}>
                    {r.rows.length} row{r.rows.length !== 1 ? 's' : ''} returned
                  </div>
                </div>
              );
            }
            return null;
          })}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{
          padding: '12px 16px', borderTop: '1px solid rgba(250,250,248,0.08)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span style={{ color: '#5b8fc4', fontSize: '11px' }}>SQL{'>'}</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') execute(); if (e.key === 'Escape') onClose(); }}
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: 'rgba(250,250,248,0.9)', fontSize: '12px',
              fontFamily: 'var(--font-mono)',
            }}
            placeholder="SELECT * FROM experience WHERE start_year > 2022"
          />
        </div>
      </div>
    </div>
  );
}
